import { useMemo, useCallback } from "react";
import { useCurrentUser } from "@/hooks/use-current-user";
import type { Action, ScopedDomain, ScopedVerb, MyRole } from "@/types/permissions";

interface ProjectWithPermissions {
  owner: string;
  my_roles: MyRole[];
}

export function usePermissions(project: ProjectWithPermissions) {
  const { data: currentUser } = useCurrentUser();
  const isOwner = currentUser?.id === project.owner;

  const actions = useMemo(
    () =>
      new Set(
        project.my_roles.flatMap((r) => r.permissions.map((p) => p.action)),
      ),
    [project.my_roles],
  );

  const can = useCallback(
    (action: Action) => isOwner || actions.has(action),
    [isOwner, actions],
  );

  return { can, isOwner, roles: project.my_roles };
}

export function useCanOnResource(project: ProjectWithPermissions) {
  const { can } = usePermissions(project);
  const { data: currentUser } = useCurrentUser();

  return useCallback(
    (domain: ScopedDomain, verb: ScopedVerb, createdBy: string) => {
      const scope = createdBy === currentUser?.id ? "Self" : "Other";
      return can(`${domain}:${verb}:${scope}` as Action);
    },
    [can, currentUser?.id],
  );
}
